import DOMPurify from "dompurify"

/**
 * Safe rendering of user-entered description / free text into a UITextView.
 * UITextView assigns its `text` to `innerHTML`, so any raw markup in stored
 * values would otherwise be parsed as HTML — a stored-XSS sink (e.g.
 * `<img src=x onerror=…>` executing in every viewer's browser).
 *
 * Two render formats:
 *  - **plaintext** (legacy / absent `descriptionFormat`): the value is plain
 *    text → `escapedHTML` entity-encodes it so it renders verbatim and inert.
 *  - **html** (rich text from the Quill editor): the value is HTML → `sanitizedHTML`
 *    runs it through DOMPurify with a strict **allowlist** (only the tags/attrs
 *    the editor toolbar can produce), stripping `<script>`, event handlers,
 *    `javascript:` URLs, and everything else.
 *
 * `plainText` strips all markup to readable text for condensed one-line surfaces
 * (offer preview / result titles) where rich formatting is not wanted.
 *
 * NOTE: `escapedHTML` is HTML *escaping* (entity encoding) — the correct, safe
 * operation for plain text. `sanitizedHTML` is HTML *sanitisation* (allowlist) —
 * for trusted-shape rich HTML. Never hand-roll tag stripping with regex.
 */
export class RHTextSanitizer {

    // Only what the rich-text toolbar can produce (bold/italic/underline/strike,
    // lists, links, headings, alignment/indent via Quill's ql-* classes).
    static readonly _allowedTags = [
        "p", "br", "span", "div",
        "strong", "b", "em", "i", "u", "s",
        "ol", "ul", "li",
        "a", "blockquote",
        "h1", "h2", "h3"
    ]
    // `class` carries Quill's ql-align-*/ql-indent-* formatting; `data-list`
    // distinguishes ordered vs bullet list items in Quill 2 output.
    static readonly _allowedAttributes = ["href", "target", "rel", "class", "data-list"]

    static escapedHTML(text: string): string {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;")
    }

    static sanitizedHTML(html: string): string {
        return DOMPurify.sanitize(html, {
            ALLOWED_TAGS: RHTextSanitizer._allowedTags,
            ALLOWED_ATTR: RHTextSanitizer._allowedAttributes
        })
    }

    static plainText(html: string): string {
        return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    }

}
